import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  TrendingUp as ProfitIcon,
  AccountBalance as BalanceIcon,
  Schedule as AgedIcon,
  ShowChart as CashFlowIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { exportToPDF, exportToExcel, exportToCSV, ExportFormat } from '../../utils/exportUtils';
import ProfitLossReport from './ProfitLossReport';
import BalanceSheetReport from './BalanceSheetReport';
import AgedDebtorsReport from './AgedDebtorsReport';
import CashFlowReport from './CashFlowReport';

// Types
interface ReportPeriod {
  start_date: Date;
  end_date: Date;
  period_type: 'custom' | 'month' | 'quarter' | 'year';
}

const AdvancedReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>({
    start_date: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    end_date: new Date(),
    period_type: 'year'
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const reportTabs = [
    {
      label: 'Profit & Loss',
      icon: <ProfitIcon />,
      component: ProfitLossReport,
      description: 'Income statement showing revenue, expenses, and net profit'
    },
    {
      label: 'Balance Sheet',
      icon: <BalanceIcon />,
      component: BalanceSheetReport,
      description: 'Financial position showing assets, liabilities, and equity'
    },
    {
      label: 'Aged Debtors',
      icon: <AgedIcon />,
      component: AgedDebtorsReport,
      description: 'Outstanding customer balances aged by invoice date'
    },
    {
      label: 'Cash Flow',
      icon: <CashFlowIcon />,
      component: CashFlowReport,
      description: 'Cash movements and liquidity analysis'
    }
  ];

  const handlePeriodTypeChange = (periodType: string) => {
    const now = new Date();
    let startDate: Date;
    let endDate = now;

    switch (periodType) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return; // Keep current dates for custom
    }

    setReportPeriod({
      start_date: startDate,
      end_date: endDate,
      period_type: periodType as any
    });
  };

  const handleExportReport = async (format: 'pdf' | 'excel' | 'csv') => {
    const reportName = reportTabs[activeTab].label;
    const reportType = reportTabs[activeTab].label.toLowerCase().replace(/\s+/g, '-').replace('&', '');

    try {
      // Fetch the report data based on the active tab
      let reportData = null;
      let reportInfo = null;

      if (reportType.includes('profit')) {
        const response = await fetch(
          `/api/v1/reports/advanced/profit-loss?start_date=${reportPeriod.start_date.toISOString().split('T')[0]}&end_date=${reportPeriod.end_date.toISOString().split('T')[0]}`
        );
        if (response.ok) {
          reportData = await response.json();
          reportInfo = {
            title: 'Profit & Loss Report',
            period: `${reportPeriod.start_date.toLocaleDateString('en-US')} to ${reportPeriod.end_date.toLocaleDateString('en-US')}`,
            data: reportData,
            type: 'profit-loss' as const
          };
        }
      } else if (reportType.includes('balance')) {
        const response = await fetch(
          `/api/v1/reports/advanced/balance-sheet?as_of_date=${reportPeriod.end_date.toISOString().split('T')[0]}`
        );
        if (response.ok) {
          reportData = await response.json();
          reportInfo = {
            title: 'Balance Sheet Report',
            period: `As of ${reportPeriod.end_date.toLocaleDateString('en-US')}`,
            data: reportData,
            type: 'balance-sheet' as const
          };
        }
      } else if (reportType.includes('cash')) {
        const response = await fetch(
          `/api/v1/reports/advanced/cash-flow?start_date=${reportPeriod.start_date.toISOString().split('T')[0]}&end_date=${reportPeriod.end_date.toISOString().split('T')[0]}`
        );
        if (response.ok) {
          reportData = await response.json();
          reportInfo = {
            title: 'Cash Flow Report',
            period: `${reportPeriod.start_date.toLocaleDateString('en-US')} to ${reportPeriod.end_date.toLocaleDateString('en-US')}`,
            data: reportData,
            type: 'cash-flow' as const
          };
        }
      } else if (reportType.includes('aged')) {
        const response = await fetch(
          `/api/v1/reports/advanced/aged-debtors?as_of_date=${reportPeriod.end_date.toISOString().split('T')[0]}`
        );
        if (response.ok) {
          reportData = await response.json();
          reportInfo = {
            title: 'Aged Debtors Report',
            period: `As of ${reportPeriod.end_date.toLocaleDateString('en-US')}`,
            data: reportData,
            type: 'aged-debtors' as const
          };
        }
      }

      if (reportInfo) {
        // Call the appropriate export function
        switch (format) {
          case 'pdf':
            exportToPDF(reportInfo);
            break;
          case 'excel':
            exportToExcel(reportInfo);
            break;
          case 'csv':
            exportToCSV(reportInfo);
            break;
        }

        setSnackbar({
          open: true,
          message: `${reportName} exported successfully as ${format.toUpperCase()}!`,
          severity: 'success'
        });
      } else {
        throw new Error('Failed to fetch report data');
      }
    } catch (error) {
      console.error('Export failed:', error);
      setSnackbar({
        open: true,
        message: `Failed to export ${reportName}. Please try again.`,
        severity: 'error'
      });
    }
  };

  const handleEmailReport = () => {
    const reportName = reportTabs[activeTab].label;
    setSnackbar({
      open: true,
      message: `${reportName} sent via email!`,
      severity: 'success'
    });
  };

  const ActiveReportComponent = reportTabs[activeTab].component;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Advanced Financial Reports
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Comprehensive financial analysis and reporting suite
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleEmailReport}
            >
              Email
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportReport('pdf')}
            >
              Export PDF
            </Button>
          </Box>
        </Box>

        {/* Report Period Controls */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Report Period
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Period Type</InputLabel>
                  <Select
                    value={reportPeriod.period_type}
                    label="Period Type"
                    onChange={(e) => handlePeriodTypeChange(e.target.value)}
                  >
                    <MenuItem value="month">This Month</MenuItem>
                    <MenuItem value="quarter">This Quarter</MenuItem>
                    <MenuItem value="year">This Year</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={reportPeriod.start_date}
                  onChange={(date) => setReportPeriod({
                    ...reportPeriod,
                    start_date: date || new Date(),
                    period_type: 'custom'
                  })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={reportPeriod.end_date}
                  onChange={(date) => setReportPeriod({
                    ...reportPeriod,
                    end_date: date || new Date(),
                    period_type: 'custom'
                  })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => setLoading(!loading)}
                  fullWidth
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Refresh'}
                </Button>
              </Grid>

              <Grid item xs={12} md={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Period: {Math.ceil((reportPeriod.end_date.getTime() - reportPeriod.start_date.getTime()) / (1000 * 60 * 60 * 24))} days
                  </Typography>
                  <Chip
                    label={reportPeriod.period_type.toUpperCase()}
                    size="small"
                    color="primary"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Report Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              {reportTabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                  sx={{ minHeight: 72 }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Report Description */}
          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="textSecondary">
              {reportTabs[activeTab].description}
            </Typography>
          </Box>

          {/* Active Report Content */}
          <CardContent>
            <ActiveReportComponent
              startDate={reportPeriod.start_date}
              endDate={reportPeriod.end_date}
              loading={loading}
              onExport={(format: string) => handleExportReport(format as 'pdf' | 'excel' | 'csv')}
            />
          </CardContent>
        </Card>

        {/* Export Options */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Export Options
            </Typography>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => handleExportReport('pdf')}
                  startIcon={<DownloadIcon />}
                >
                  PDF Report
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => handleExportReport('excel')}
                  startIcon={<DownloadIcon />}
                >
                  Excel Spreadsheet
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => handleExportReport('csv')}
                  startIcon={<DownloadIcon />}
                >
                  CSV Data
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AdvancedReports;
